/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, createStyles, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Menu, MenuItem, Theme, withStyles, WithStyles } from "@material-ui/core";
import CloudOffIcon from "@material-ui/icons/CloudOff";
import CloudQueueIcon from "@material-ui/icons/CloudQueue";
import Delete from "@material-ui/icons/Delete";
import More from "@material-ui/icons/MoreVert";
import Sync from "@material-ui/icons/Sync";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { createSelector } from 'reselect';
import { SyncStatus } from "../../evaluation";
import { AppState, deleteEvaluation, setActiveEval, syncEvaluation } from "../../redux";
import { getListItemValues } from "../../utils/interface";
import { momentShortFormat } from "../../utils/time";

moment.updateLocale("en", momentShortFormat);

const styles = (theme: Theme) =>
  createStyles({
    moreButton: {
      color: theme.palette.divider,
      "&:hover": {
        color: theme.palette.text.secondary,
      },
    },
    actionButton: {
      textTransform: "none",
    },
    disableListItem: {
      color: theme.palette.text.disabled
    }
  });

interface EvalListItemProps extends WithStyles<typeof styles> {
  evalID: string;
}
const selectEvals = (state: AppState) => state.evals.items;

export const selectCertifications = (state: AppState, _: any) => state.session.dfdmToken!.certifications ?? state.session.dfdmToken!.evaluations;

export const selectFormType = (state: AppState, props: EvalListItemProps) => {
  if (!state.evals.items.has(props.evalID)) {
    return null;
  }

  const evalMeta = state.evals.items.get(props.evalID)!;
  return evalMeta.formType;
};

// dont allow publish for expired, not in training
// allow new or legacy format for cert statuses
const selectCertificationStatus = createSelector(
  selectCertifications,
  selectFormType,
  (certifications, formType) => {
  if (!formType) {
    return false;
  } else {
    return certifications[formType];
  }
})

export const selectCanRead = createSelector(
  selectCertificationStatus,
  status => {
    if (!status) {
      return false;
    }
    if (Array.isArray(status)) {
      if (status.findIndex(status => status !== 'expired') >= 0) {
        return true;
      }
    } else if ((status as any).allow_read) {
      return true;
    }
    return false;
  }
)

export default withStyles(styles)((props: EvalListItemProps) => {
  const { evalID } = props;

  const evalData = useSelector(
    createSelector(selectEvals, items => items.getIn([evalID, "data"]))
  );
  const published = useSelector(
    createSelector(selectEvals, items => items.getIn([evalID, "published"]))
  );

  const dbPublished = useSelector(
    createSelector(selectEvals, items => items.getIn([evalID, "dbPublished"]))
  );

  const canRead = useSelector<AppState, any>(state => selectCanRead(state, props));

  const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [cancelHandler, setCancelHandler] = useState<() => void>();
  
  const handleSelectClose = () => {
    setDialogContent(null);
  };

  /**
   * The definition associated to a given eval list item may be null temporarily
   */
  const form = useSelector((state: AppState) => {
    if (!state.evals.items.has(evalID)) {
      return null;
    }

    const evalMeta = state.evals.items.get(evalID)!;
    return state.forms.items[evalMeta.formType]?.[evalMeta.formVersion] ?? null;
  });

  const syncStatus: SyncStatus | undefined = useSelector((state: AppState) =>
    state.evals.items.getIn([evalID, "syncStatus"])
  );

  const {primary, secondary} = useMemo(
    function update() {
      if (!form) {
        return { primary: '', secondary: ''};
      }
      return getListItemValues(evalData, form!);
    },
    [evalData, form]
  );

  const [menuAnchorElement, setMenuAnchorElement] = useState<Element | null>(null);

  const [dialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dialogOpenCert, setCertificationDialogOpen] = useState<boolean>(false);

  function handleClose(event: Event) {
    event.stopPropagation();
    setMenuAnchorElement(null);
  }

  const dispatch = useDispatch();
  const history = useHistory();

  function handleClick() {
    if (canRead) {
      dispatch(setActiveEval(evalID, history));
    } else {
      setCertificationDialogOpen(true);
    }
  }

  function handleSync() {
    dispatch(syncEvaluation(evalID));
  }

  function handleDelete() {
    setDeleteDialogOpen(true);
  }

  return !form ? null : (
    <ListItem button onClick={handleClick} className={!canRead ? props.classes.disableListItem : ''}>
      <Dialog
        id="confirm-delete-eval"
        open={dialogOpen}
        maxWidth="sm"
        fullWidth
        onClose={(event: any) => {
          event.stopPropagation();
          setDeleteDialogOpen(false);
        }}
      >
        <DialogTitle>Delete ACC Audit</DialogTitle>
        <DialogContent>
          {!dbPublished && (
          <DialogContentText>
            Are you sure you want to delete this ACC Audit? It has not been submitted and any data entered will be lost.
          </DialogContentText>
          )}
          {dbPublished && (
          <DialogContentText>
            This ACC Audit was previously submitted to the database. Deleting it will remove it from your device without your changes being submitted to the database. The previously submitted ACC Audit will remain attached to the facility.
            If you believe that this ACC Audit should be deleted from the database, please email dairyfarm@nmpf.org for assistance.
          </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className={props.classes.actionButton}
            onClick={event => {
              event.stopPropagation();
              setDeleteDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className={props.classes.actionButton}
            onClick={event => {
              event.stopPropagation();
              dispatch(deleteEvaluation.request({ id: evalID, type: form.form_id }));
              setDeleteDialogOpen(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        id="unauthorized-eval"
        open={dialogOpenCert}
        maxWidth="sm"
        fullWidth
        onClose={(event: any) => {
          event.stopPropagation();
          setCertificationDialogOpen(false);
        }}
      >
        <DialogTitle>Access Denied: Certification Expired</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You do not have a current certification on record and may not view or submit ACC Audits. Please email <a href={`mailto:farmtechsupport@nmpf.org}`}> farmtechsupport@nmpf.org </a> with the contents of this message.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={props.classes.actionButton}
            onClick={event => {
              event.stopPropagation();
              setCertificationDialogOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {syncStatus === 'FAILED' && (
        <ListItemIcon>
          <CloudOffIcon />
        </ListItemIcon>
      )}
      {syncStatus === 'PENDING' && (
        <ListItemIcon>
          <CloudQueueIcon />
        </ListItemIcon>
      )}
      <ListItemText primary={primary} secondary={secondary} classes={{primary: !canRead ? props.classes.disableListItem : '', secondary: !canRead ? props.classes.disableListItem : ''}}/>
      <Menu
        id="new-menu"
        anchorEl={menuAnchorElement}
        keepMounted
        open={Boolean(menuAnchorElement)}
        onClose={handleClose}
      >
        <MenuItem
          onClick={event => {
            event.stopPropagation();
            handleSync();
            setMenuAnchorElement(null);
          }}
        >
          <ListItemIcon>
            <Sync fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sync" />
        </MenuItem>

        <Divider />
        {!published && (
          <MenuItem
            onClick={event => {
              event.stopPropagation();
              handleDelete();
              setMenuAnchorElement(null);
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>

      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          aria-label="menu"
          onClick={event => {
            event.stopPropagation();
            setMenuAnchorElement(event.currentTarget);
          }}
          className={props.classes.moreButton}
        >
          <More />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
});
